import React, { useState } from "react";
import "./CreateUserForm.css";
import { createUser, type UserRole } from "../../../Services/Admin/users";

/**
 * Skapar ett konto med en av rollerna den inloggade får hantera: SUPERADMIN
 * skapar admins och nedåt, ADMIN skapar contributors och users.
 *
 * Fälten hade tidigare inloggningssidans klasser, som är byggda för en
 * centrerad ruta på 20 rem och blev godtyckligt smala här. Nu används
 * adminens egna fält, och varje fält har en etikett i stället för bara en
 * platshållare — den försvinner så fort man börjat skriva.
 */

const ROLE_LABELS: Record<UserRole, string> = {
  USER: "User",
  CONTRIBUTOR: "Contributor",
  ADMIN: "Admin",
  SUPERADMIN: "Superadmin",
};

type Props = {
  allowedRoles: UserRole[];
  onCreated?: () => void;
};

export default function CreateUserForm({ allowedRoles, onCreated }: Props) {
  const [role, setRole] = useState<UserRole>(
    allowedRoles.includes("CONTRIBUTOR") ? "CONTRIBUTOR" : allowedRoles[0]
  );
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [yob, setYob] = useState<number | "">("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const currentYear = new Date().getFullYear();
  const minAge = 18;
  const maxYear = currentYear - minAge;
  const startYear = 1900;

  const years = Array.from({ length: maxYear - startYear + 1 }, (_, i) => maxYear - i);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const yearOfBirth = Number(yob);
    if (!Number.isInteger(yearOfBirth) || String(yearOfBirth).length !== 4) {
      setError("Year of birth must be a 4-digit year");
      return;
    }

    setLoading(true);
    try {
      const res = await createUser({
        role,
        email: email.trim().toLowerCase(),
        password,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        yearOfBirth: yearOfBirth,
      });

      setSuccess(`Created ${ROLE_LABELS[res.user.role].toLowerCase()}: ${res.user.email}`);
      setEmail("");
      setPassword("");
      setFirstName("");
      setLastName("");
      setYob("");
      onCreated?.();
    } catch (e: any) {
      setError(e?.message ?? "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="contributor-form" onSubmit={onSubmit}>
      <label className="admin-field">
        <span className="admin-label">Role</span>
        <select value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
          {allowedRoles.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
      </label>

      <label className="admin-field">
        <span className="admin-label">Email</span>
        <input
          type="email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>

      <div className="contributor-row">
        <label className="admin-field">
          <span className="admin-label">First name</span>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </label>

        <label className="admin-field">
          <span className="admin-label">Last name</span>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </label>
      </div>

      <div className="contributor-row">
        <label className="admin-field">
          <span className="admin-label">Password</span>
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        <label className="admin-field">
          <span className="admin-label">Year of birth</span>
          <select value={yob} onChange={(e) => setYob(Number(e.target.value))} required>
            <option value="" disabled>
              Select a year
            </option>

            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {success && <div className="admin-notice">{success}</div>}

      <div className="contributor-actions">
        <button className="admin-btn--primary" type="submit" disabled={loading}>
          {loading ? "Creating…" : `Create ${ROLE_LABELS[role].toLowerCase()}`}
        </button>
      </div>
    </form>
  );
}
